/**
 * Segments (B6, ADR-052) — whitelist-compiled player segments.
 *
 * The editor builds a flat AND-list of `{ field, op, value }` rows. The list
 * of pickable fields comes from the SERVER's whitelist metadata — this screen
 * cannot express anything the compiler's map does not contain, and no SQL
 * exists anywhere on this side. Preview is live against the draft; export is
 * a moment-in-time CSV; the one action is a batch bonus-grant THROUGH the
 * promo service, where the campaign's own budget cap and guards apply.
 */
import { useState, type ReactNode } from 'react';
import { fetchCampaigns } from '../api/client.ts';
import type { Currency } from '../api/types.ts';
import {
  createSegment,
  deleteSegment,
  exportCsv,
  fetchSegments,
  grantToSegment,
  previewPredicates,
  updateSegment,
  type SegmentFieldMeta,
  type SegmentOp,
  type SegmentPredicate,
  type SegmentPreview,
  type SegmentView,
} from '../api/segments.ts';
import { useAsync } from '../lib/useAsync.ts';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { Card, ErrorState, Loading } from '../ui/primitives.tsx';
import { IconPeople, IconSearch, IconTrend } from '../ui/icons.tsx';

const OP_LABEL: Record<SegmentOp, string> = { eq: 'is', neq: 'is not', gte: '≥', lte: '≤' };

/** Which input widget each v1 field takes. The server whitelist stays the truth
 * of what EXISTS; this only picks the control. An unlisted future field falls
 * back to a number input. */
const EDITOR: Record<string, 'number' | 'bool' | 'status' | 'country' | 'money'> = {
  created_days_ago: 'number',
  last_bet_days_ago: 'number',
  bet_count: 'number',
  vip_tier: 'number',
  marketing_opt_in: 'bool',
  has_deposited: 'bool',
  status: 'status',
  country_code: 'country',
  deposited_total: 'money',
  turnover: 'money',
  balance: 'money',
};

const CURRENCIES: Currency[] = ['INR', 'USDT', 'USD', 'EUR', 'BRL'];

function defaultValue(field: string): unknown {
  switch (EDITOR[field] ?? 'number') {
    case 'number':
      return 0;
    case 'bool':
      return true;
    case 'status':
      return 'active';
    case 'country':
      return 'IN';
    case 'money':
      return { currency: 'INR', amount: '0' };
  }
}

export function Segments(): ReactNode {
  const listing = useAsync(() => fetchSegments(), []);
  const [selected, setSelected] = useState<number | 'new' | null>(null);

  if (listing.error) {
    return <ErrorState title="Could not load segments" detail={listing.error} onRetry={listing.reload} />;
  }
  if (listing.loading || !listing.data) return <Loading>Loading segments…</Loading>;

  const { segments, fields } = listing.data;
  const current = selected === 'new' ? null : segments.find((s) => s.id === selected) ?? null;

  return (
    <div className="stack">
      <Card title="Segments" icon={<IconPeople />} sub="A segment is a saved query — membership is computed the moment you use it, never stored">
        {segments.length === 0 ? (
          <p className="field__hint">No segments yet.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Conditions</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {segments.map((s) => (
                <tr key={s.id} className={s.id === selected ? 'is-selected' : undefined}>
                  <td>{s.name}</td>
                  <td className="field__hint">{s.predicates.map((p) => `${p.field} ${OP_LABEL[p.op]} ${JSON.stringify(p.value)}`).join(' AND ')}</td>
                  <td>
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => setSelected(s.id)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="row-actions row-actions--start">
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => setSelected('new')}>
            New segment
          </button>
        </div>
      </Card>

      {selected !== null ? (
        <Editor
          key={selected === 'new' ? 'new' : current?.id ?? 'missing'}
          segment={current}
          fields={fields}
          onDone={() => {
            setSelected(null);
            listing.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function Editor({
  segment,
  fields,
  onDone,
}: {
  segment: SegmentView | null;
  fields: SegmentFieldMeta[];
  onDone: () => void;
}): ReactNode {
  const [name, setName] = useState(segment?.name ?? '');
  const [predicates, setPredicates] = useState<SegmentPredicate[]>(
    segment?.predicates.map((p) => ({ ...p })) ?? [],
  );
  const [preview, setPreview] = useState<SegmentPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const ready = name.trim() !== '' && predicates.length > 0;

  async function onPreview(): Promise<void> {
    if (predicates.length === 0 || busy) return;
    setBusy(true);
    try {
      setPreview(await previewPredicates(predicates));
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Preview failed.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onSave(): Promise<void> {
    if (!ready || busy) return;
    setBusy(true);
    try {
      if (segment) await updateSegment(segment.id, name.trim(), predicates);
      else await createSegment(name.trim(), predicates);
      toast('Segment saved.');
      onDone();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not save the segment.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(): Promise<void> {
    if (!segment || busy) return;
    const result = await confirm({
      title: 'Delete this segment',
      intent: 'danger',
      body: 'The definition is removed. No player, grant or ledger row is touched — a segment is only a query.',
      confirmLabel: 'Delete',
      facts: [{ label: 'Segment', value: segment.name }],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;
    setBusy(true);
    try {
      await deleteSegment(segment.id);
      toast('Segment deleted.');
      onDone();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not delete the segment.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function onExport(): Promise<void> {
    if (!segment || busy) return;
    setBusy(true);
    try {
      const csv = await exportCsv(segment.id);
      const blob = new Blob([csv], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `segment-${segment.id}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Export failed.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={segment ? `Edit · ${segment.name}` : 'New segment'}
      icon={<IconSearch />}
      sub="Every condition must hold (AND). Fields come from the server's whitelist — nothing else can be expressed"
    >
      <label className="field">
        <span className="field__label">Name</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Dormant INR depositors" />
      </label>

      {predicates.map((p, i) => (
        <PredicateRow
          key={i}
          predicate={p}
          fields={fields}
          onChange={(next) => setPredicates((list) => list.map((row, n) => (n === i ? next : row)))}
          onRemove={() => setPredicates((list) => list.filter((_, n) => n !== i))}
        />
      ))}

      <div className="row-actions row-actions--start">
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          onClick={() => {
            const first = fields[0];
            if (!first) return;
            setPredicates((list) => [...list, { field: first.field, op: first.ops[0]!, value: defaultValue(first.field) }]);
          }}
        >
          Add condition
        </button>
        <button type="button" className="btn btn--sm btn--ghost" disabled={busy || predicates.length === 0} onClick={() => void onPreview()}>
          Preview
        </button>
        <button type="button" className="btn btn--sm btn--primary" disabled={busy || !ready} onClick={() => void onSave()}>
          {segment ? 'Save changes' : 'Create segment'}
        </button>
        {segment ? (
          <>
            <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => void onExport()}>
              Export CSV
            </button>
            <button type="button" className="btn btn--sm btn--danger" disabled={busy} onClick={() => void onDelete()}>
              Delete
            </button>
          </>
        ) : null}
      </div>

      {preview ? (
        <div className="subcard">
          <p className="field__hint">
            <strong>{preview.total.toLocaleString()}</strong> member{preview.total === 1 ? '' : 's'} right now
            {preview.sample.length > 0 ? ` — first ${preview.sample.length}:` : '.'}
          </p>
          {preview.sample.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Signed up</th>
                </tr>
              </thead>
              <tbody>
                {preview.sample.map((m) => (
                  <tr key={m.userId}>
                    <td>{m.username}</td>
                    <td className="num">{m.createdAt.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}

      {segment ? <GrantPanel segmentId={segment.id} segmentName={segment.name} /> : null}
    </Card>
  );
}

function PredicateRow({
  predicate,
  fields,
  onChange,
  onRemove,
}: {
  predicate: SegmentPredicate;
  fields: SegmentFieldMeta[];
  onChange: (next: SegmentPredicate) => void;
  onRemove: () => void;
}): ReactNode {
  const meta = fields.find((f) => f.field === predicate.field);
  const editor = EDITOR[predicate.field] ?? 'number';
  const moneyVal = editor === 'money' ? (predicate.value as { currency: Currency; amount: string }) : null;

  return (
    <div className="row-actions row-actions--start">
      <select
        className="input input--sm"
        value={predicate.field}
        title={meta?.describe}
        onChange={(e) => {
          const field = e.target.value;
          const next = fields.find((f) => f.field === field);
          onChange({ field, op: next?.ops[0] ?? 'eq', value: defaultValue(field) });
        }}
      >
        {fields.map((f) => (
          <option key={f.field} value={f.field} title={f.describe}>
            {f.field}
          </option>
        ))}
      </select>
      <select
        className="input input--sm"
        value={predicate.op}
        onChange={(e) => onChange({ ...predicate, op: e.target.value as SegmentOp })}
      >
        {(meta?.ops ?? ['eq']).map((op) => (
          <option key={op} value={op}>
            {OP_LABEL[op]}
          </option>
        ))}
      </select>

      {editor === 'number' ? (
        <input
          className="input input--sm num"
          inputMode="numeric"
          value={String(predicate.value ?? 0)}
          onChange={(e) => onChange({ ...predicate, value: Number(e.target.value.replace(/\D/g, '') || '0') })}
        />
      ) : null}
      {editor === 'bool' ? (
        <select
          className="input input--sm"
          value={String(predicate.value)}
          onChange={(e) => onChange({ ...predicate, value: e.target.value === 'true' })}
        >
          <option value="true">yes</option>
          <option value="false">no</option>
        </select>
      ) : null}
      {editor === 'status' ? (
        <select
          className="input input--sm"
          value={String(predicate.value)}
          onChange={(e) => onChange({ ...predicate, value: e.target.value })}
        >
          <option value="active">active</option>
          <option value="suspended">suspended</option>
          <option value="closed">closed</option>
        </select>
      ) : null}
      {editor === 'country' ? (
        <input
          className="input input--sm"
          maxLength={2}
          value={String(predicate.value ?? '')}
          onChange={(e) => onChange({ ...predicate, value: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })}
        />
      ) : null}
      {editor === 'money' && moneyVal ? (
        <>
          <select
            className="input input--sm"
            value={moneyVal.currency}
            onChange={(e) => onChange({ ...predicate, value: { ...moneyVal, currency: e.target.value as Currency } })}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <input
            className="input input--sm num"
            inputMode="decimal"
            value={moneyVal.amount}
            onChange={(e) => onChange({ ...predicate, value: { ...moneyVal, amount: e.target.value.trim() } })}
          />
        </>
      ) : null}

      <button type="button" className="btn btn--sm btn--ghost" onClick={onRemove} title="Remove condition">
        ×
      </button>
    </div>
  );
}

function GrantPanel({ segmentId, segmentName }: { segmentId: number; segmentName: string }): ReactNode {
  const campaigns = useAsync(() => fetchCampaigns(), []);
  const [campaignId, setCampaignId] = useState<number | ''>('');
  const [currency, setCurrency] = useState<Currency>('INR');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const amountValid = /^\d+(\.\d{1,4})?$/.test(amount) && Number(amount) > 0;
  const ready = campaignId !== '' && amountValid;

  async function onGrant(): Promise<void> {
    if (!ready || busy) return;
    const campaign = campaigns.data?.find((c) => c.id === campaignId);
    const result = await confirm({
      title: 'Grant a bonus to this segment',
      body: 'One grant per current member, through the promo service — the campaign\'s own budget cap, one-active-bonus rule and claim ceilings all apply and skip anyone they protect. Capped at 5,000 members.',
      confirmLabel: 'Grant to segment',
      facts: [
        { label: 'Segment', value: segmentName },
        { label: 'Campaign', value: campaign?.name ?? String(campaignId) },
        { label: 'Bonus per member', value: `${amount} ${currency}` },
      ],
      note: { label: 'Reason', required: true },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      const r = await grantToSegment(segmentId, campaignId, currency, amount);
      const skipped = Object.entries(r.skipped)
        .map(([reason, n]) => `${reason} ${n}`)
        .join(', ');
      toast(`Granted ${r.created} of ${r.members}${skipped ? ` · skipped: ${skipped}` : ''}${r.failed ? ` · failed ${r.failed}` : ''}.`);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'The batch grant failed.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="subcard">
      <p className="field__hint">
        <IconTrend size="sm" /> <strong>Batch bonus grant</strong> — through the promo service; the
        campaign's budget cap is the ceiling, exactly as on every other grant path.
      </p>
      <div className="row-actions row-actions--start">
        <select
          className="input input--sm"
          value={campaignId === '' ? '' : String(campaignId)}
          onChange={(e) => setCampaignId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">Pick a campaign…</option>
          {(campaigns.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.enabled ? '' : ' (disabled)'}
            </option>
          ))}
        </select>
        <select className="input input--sm" value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <input
          className="input input--sm num"
          placeholder="Bonus per member"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.trim())}
        />
        <button type="button" className="btn btn--sm btn--primary" disabled={busy || !ready} onClick={() => void onGrant()}>
          {busy ? 'Granting…' : 'Grant to segment'}
        </button>
      </div>
    </div>
  );
}
